import React, { useState, useEffect } from 'react';
import { Card, GameState, PlayerSeat, TrickPlay } from '../../core/types';
import { PlayingCard } from '../Card/PlayingCard';
import { describeCombo } from '../../core/combos';
import { choosePlan } from '../../core/optimizer';
import { Sound } from '../../core/audio';
import { Voice, POPULAR_EMOJIS, POPULAR_PHRASES, VoicePhrase } from '../../core/voice';
import { sortHand } from '../../core/cards';
import { CardBackCustomizerModal } from '../Card/CardBackCustomizerModal';
import { useI18n } from '../../core/i18n';
import {
  Crown,
  Sparkles,
  Volume2,
  VolumeX,
  X,
  Eye,
  EyeOff,
  Smile,
  MessageCircle,
  Zap,
  Layers,
  ArrowDownWideNarrow,
  Plus,
  RotateCcw,
  Maximize2,
  Minimize2,
  Palette,
} from 'lucide-react';

export type GroupingMode = 'natural' | 'coach' | 'manual';

interface PokerTableProps {
  gameState: GameState;
  selectedIds: Set<string>;
  onToggleCard: (cardId: string) => void;
  onClearSelection: () => void;
  onPlay: () => void;
  onPass: () => void;
  onAutoHint: () => void;
  onToggleGodMode?: () => void;
  onSendEmoji?: (seat: PlayerSeat, emoji: string, text?: string) => void;
}

export const PokerTable: React.FC<PokerTableProps> = ({
  gameState,
  selectedIds,
  onToggleCard,
  onClearSelection,
  onPlay,
  onPass,
  onAutoHint,
  onToggleGodMode,
  onSendEmoji,
}) => {
  const { t } = useI18n();
  const [groupingMode, setGroupingMode] = useState<GroupingMode>(() => {
    const saved = localStorage.getItem('guandan_grouping_mode');
    if (saved === 'natural' || saved === 'coach' || saved === 'manual') return saved;
    return 'coach';
  });
  const [isLandscapeMode, setIsLandscapeMode] = useState<boolean>(false);
  const [customGroups, setCustomGroups] = useState<Card[][]>([]);
  const [isMuted, setIsMuted] = useState<boolean>(() => Sound.getIsMuted());
  const [showEmojiPicker, setShowEmojiPicker] = useState<boolean>(false);
  const [showVoicePicker, setShowVoicePicker] = useState<boolean>(false);
  const [showCardBackModal, setShowCardBackModal] = useState<boolean>(false);
  const [activeEmojis, setActiveEmojis] = useState<Record<number, { emoji: string; text?: string } | null>>({});
  const [countdown, setCountdown] = useState<number>(20);
  const [isDealing, setIsDealing] = useState<boolean>(true);
  const [dealProgress, setDealProgress] = useState<number>(0);

  const {
    hands,
    currentTurn,
    currentCombo,
    trickPlays,
    levelRank,
    finishedOrder,
    isGodMode,
    mode = '4p',
  } = gameState;

  const is6p = mode === '6p';
  const userHand = hands[0] || [];
  const isMyTurn = currentTurn === 0;

  // Trigger distribution / dealing animation on new hand start
  useEffect(() => {
    if (gameState.history.length === 0 && userHand.length > 0) {
      setIsDealing(true);
      setDealProgress(1);
      let count = 1;
      const totalCards = userHand.length;

      const dealTimer = setInterval(() => {
        count += 1;
        if (count >= totalCards) {
          count = totalCards;
          setDealProgress(totalCards);
          setIsDealing(false);
          clearInterval(dealTimer);
          Sound.playCardPlay();
        } else {
          setDealProgress(count);
          if (count % 3 === 0) {
            Sound.playCardDeal();
          }
        }
      }, 48);

      return () => clearInterval(dealTimer);
    } else {
      setIsDealing(false);
      setDealProgress(userHand.length);
    }
  }, [gameState.history.length, userHand.length]);

  const handleSkipDealing = () => {
    setIsDealing(false);
    setDealProgress(userHand.length);
    Sound.playCardPlay();
  };

  const handleSelectGroupingMode = (mode: GroupingMode) => {
    setGroupingMode(mode);
    localStorage.setItem('guandan_grouping_mode', mode);
  };

  const handleToggleLandscape = () => {
    setIsLandscapeMode((prev) => !prev);
    Sound.playCardClick();
  };

  useEffect(() => {
    setCountdown(20);
    const interval = setInterval(() => {
      setCountdown((prev) => (prev > 0 ? prev - 1 : 20));
    }, 1000);
    return () => clearInterval(interval);
  }, [currentTurn]);

  useEffect(() => {
    const userHandCardIds = new Set(userHand.map((c) => c.id));
    setCustomGroups((prevGroups) =>
      prevGroups
        .map((group) => group.filter((c) => userHandCardIds.has(c.id)))
        .filter((group) => group.length > 0)
    );
  }, [userHand]);

  const visibleHand = isDealing ? userHand.slice(0, dealProgress) : userHand;
  const planResult = choosePlan(visibleHand, levelRank);
  const bestGroups = planResult.best.groups;
  const naturalSortedHand = sortHand([...visibleHand], levelRank, true);

  const activeCustomGroupCardIds = new Set(customGroups.flatMap((g) => g.map((c) => c.id)));
  const ungroupedCards = naturalSortedHand.filter((c) => !activeCustomGroupCardIds.has(c.id));

  const handleCreateCustomGroup = () => {
    if (selectedIds.size === 0) return;
    const cardsToGroup = userHand.filter((c) => selectedIds.has(c.id));
    if (cardsToGroup.length === 0) return;

    const cleanOldGroups = customGroups
      .map((g) => g.filter((c) => !selectedIds.has(c.id)))
      .filter((g) => g.length > 0);

    setCustomGroups([...cleanOldGroups, cardsToGroup]);
    onClearSelection();
    Sound.playCardPlay();
  };

  const handleDissolveCustomGroup = (index: number) => {
    setCustomGroups((prev) => prev.filter((_, idx) => idx !== index));
    Sound.playCardClick();
  };

  const handleResetCustomGroups = () => {
    setCustomGroups([]);
    onClearSelection();
    Sound.playCardClick();
  };

  const seatNames4p: Record<number, { name: string; pos: string; role: string }> = {
    0: { name: '我 (南)', pos: '南', role: '己方主攻' },
    1: { name: '东家 (右家)', pos: '东', role: '对方下家' },
    2: { name: '搭档 (北门)', pos: '北', role: '对门搭档' },
    3: { name: '西家 (左家)', pos: '西', role: '对方上家' },
  };

  const seatNames6p: Record<number, { name: string; pos: string; role: string }> = {
    0: { name: '我 (南)', pos: '南', role: '主队1' },
    1: { name: '东1 (东南)', pos: '东南', role: '客队1' },
    2: { name: '搭档1 (西北)', pos: '西北', role: '主队2' },
    3: { name: '北家 (正北)', pos: '正北', role: '客队2' },
    4: { name: '搭档2 (东北)', pos: '东北', role: '主队3' },
    5: { name: '西1 (西南)', pos: '西南', role: '客队3' },
  };

  const seatNames = is6p ? seatNames6p : seatNames4p;

  const handleToggleSound = () => {
    const next = Sound.toggleMute();
    setIsMuted(next);
  };

  const triggerSeatEmoji = (seat: PlayerSeat, emoji: string, text?: string) => {
    setActiveEmojis((prev) => ({ ...prev, [seat]: { emoji, text } }));
    if (text) {
      Voice.speak(text);
    } else {
      Sound.playCardPlay();
    }
    if (onSendEmoji) onSendEmoji(seat, emoji, text);

    setTimeout(() => {
      setActiveEmojis((prev) => ({ ...prev, [seat]: null }));
    }, 3500);
  };

  const handleSelectEmoji = (item: (typeof POPULAR_EMOJIS)[0]) => {
    triggerSeatEmoji(0 as PlayerSeat, item.emoji, item.label);
    setShowEmojiPicker(false);
  };

  const handleSelectPhrase = (phrase: VoicePhrase) => {
    triggerSeatEmoji(0 as PlayerSeat, '💬', phrase.text);
    Voice.triggerPhrase(phrase);
    setShowVoicePicker(false);
  };

  // Render Per-Seat Dedicated Trick Plays & Persistent Action Status
  const renderSeatTrickAction = (seatIndex: number, extraClasses = '') => {
    const trick = trickPlays[seatIndex];
    if (!trick) return null;

    if (trick.action === 'pass') {
      return (
        <div
          className={`animate-fade-in bg-rose-950/90 text-rose-300 border border-rose-500/80 font-black text-[9px] sm:text-xs px-2 py-0.5 rounded-full shadow-lg flex items-center space-x-0.5 backdrop-blur-md ${extraClasses}`}
        >
          <span className="text-rose-400">🛑</span>
          <span>{t.passBtn}</span>
        </div>
      );
    }

    if (trick.action === 'play' && trick.cards && trick.cards.length > 0) {
      return (
        <div className={`flex flex-col items-center animate-fade-in space-y-0.5 ${extraClasses}`}>
          {trick.combo && (
            <div className="text-[8px] sm:text-[9px] font-black px-1.5 py-0.1 rounded-full bg-black/85 text-amber-300 border border-amber-500/40 shadow-sm flex items-center gap-0.5">
              <span>{describeCombo(trick.combo)}</span>
              {trick.combo.isBomb && <span className="text-rose-400 font-black">🔥</span>}
            </div>
          )}
          <div className="flex -space-x-3 sm:-space-x-4 drop-shadow-lg">
            {trick.cards.map((c) => (
              <PlayingCard key={c.id} card={c} levelRank={levelRank} size="sm" disabled />
            ))}
          </div>
        </div>
      );
    }

    return null;
  };

  // Render Seat Avatar & Info HUD with Active Turn Spotlight & Red/Blue Team Colors
  const renderSeatAvatar = (seatIndex: number, extraClasses = '') => {
    const isCurrent = currentTurn === seatIndex;
    const isFinished = finishedOrder.includes(seatIndex as PlayerSeat);
    const finishRank = finishedOrder.indexOf(seatIndex as PlayerSeat) + 1;
    const count = hands[seatIndex]?.length || 0;
    const seatInfo = seatNames[seatIndex] || { name: `Seat ${seatIndex}`, pos: '', role: '' };
    
    // Team 0 (红方): 0, 2, 4 | Team 1 (蓝方): 1, 3, 5
    const isRedTeam = seatIndex % 2 === 0;
    const isPartner = isRedTeam && seatIndex !== 0;
    const activeMsg = activeEmojis[seatIndex];

    return (
      <div className={`relative flex flex-col items-center select-none ${extraClasses}`}>
        {/* Dynamic Speech / Emoji Bubble */}
        {activeMsg && (
          <div className="absolute -top-7 z-40 bg-white text-slate-900 px-1.5 py-0.2 rounded-xl shadow-xl border border-amber-300 font-bold text-[10px] flex items-center space-x-1 animate-bounce whitespace-nowrap">
            <span>{activeMsg.emoji}</span>
            {activeMsg.text && <span>{activeMsg.text}</span>}
          </div>
        )}

        {/* Circular Avatar with Red / Blue Team Glow */}
        <div className="relative flex items-center justify-center">
          {isCurrent && (
            <div className="absolute -inset-1.5 rounded-full bg-amber-400/30 blur-sm animate-pulse pointer-events-none" />
          )}

          <div
            className={`w-7.5 h-7.5 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 z-10 ${
              isCurrent
                ? 'ring-2 ring-amber-400 bg-gradient-to-tr from-amber-500 via-amber-300 to-amber-600 scale-105 shadow-[0_0_15px_rgba(245,158,11,0.8)]'
                : isRedTeam
                ? 'ring-1.5 ring-rose-500/80 bg-gradient-to-tr from-rose-900 via-rose-800 to-rose-950 shadow-[0_0_10px_rgba(244,63,94,0.3)]'
                : 'ring-1.5 ring-sky-500/80 bg-gradient-to-tr from-sky-900 via-sky-800 to-sky-950 shadow-[0_0_10px_rgba(14,165,233,0.3)]'
            }`}
          >
            <span className="text-sm sm:text-base filter drop-shadow">
              {seatIndex === 0 ? '😎' : isPartner ? '🤝' : '🤖'}
            </span>

            {isCurrent && (
              <div className="absolute -bottom-1 -right-1 bg-amber-500 text-slate-950 font-black text-[7px] sm:text-[8px] w-3.5 h-3.5 rounded-full flex items-center justify-center border border-amber-200 shadow">
                {countdown}
              </div>
            )}
          </div>

          {isFinished && (
            <div className="absolute -top-1.5 -right-1.5 bg-gradient-to-r from-amber-400 to-amber-600 text-slate-950 font-black text-[8px] px-1 py-0.1 rounded-full shadow border border-amber-200 z-20">
              {finishRank === 1 ? '🥇' : finishRank === 2 ? '🥈' : '🥉'}
            </div>
          )}
        </div>

        {/* Seat Label Pill & Remaining Count with Red / Blue Team Badge */}
        <div className="mt-0.5 flex items-center space-x-0.5">
          <span
            className={`px-1 py-0.1 rounded text-[8px] sm:text-[9px] font-black leading-none flex items-center space-x-0.5 ${
              isCurrent
                ? 'bg-amber-500 text-slate-950'
                : isRedTeam
                ? 'bg-rose-950/90 text-rose-200 border border-rose-600/50'
                : 'bg-sky-950/90 text-sky-200 border border-sky-600/50'
            }`}
          >
            <span className="text-[7px]">{isRedTeam ? '🔴' : '🔵'}</span>
            <span>{seatInfo.name}</span>
          </span>
          {!isFinished && seatIndex !== 0 && (
            <span
              className={`text-[8px] font-mono font-bold px-1 rounded border ${
                isRedTeam
                  ? 'bg-rose-950/80 text-rose-300 border-rose-500/40'
                  : 'bg-sky-950/80 text-sky-300 border-sky-500/40'
              }`}
            >
              余{count}
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderAdaptiveCards = (cards: Card[]) => {
    const total = cards.length;
    return (
      <div className="flex items-end justify-center w-full px-1 overflow-visible">
        {cards.map((c, idx) => {
          const isLast = idx === total - 1;
          const overlapStyle = isLast
            ? {}
            : {
                marginRight:
                  total > 20
                    ? '-26px'
                    : total > 14
                    ? '-22px'
                    : total > 8
                    ? '-16px'
                    : '-10px',
                zIndex: idx + 1,
              };

          return (
            <div key={c.id} style={overlapStyle} className="transition-all duration-150 shrink-0">
              <PlayingCard
                card={c}
                levelRank={levelRank}
                isSelected={selectedIds.has(c.id)}
                onClick={() => onToggleCard(c.id)}
                size="md"
              />
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div
      className={`relative select-none overflow-hidden font-sans transition-all duration-300 w-full h-full flex flex-col justify-between ${
        isLandscapeMode
          ? 'fixed inset-0 z-50 rounded-none border-none shadow-none p-1'
          : 'rounded-2xl sm:rounded-3xl border-2 sm:border-4 border-[#2b1f14] shadow-[0_20px_50px_rgba(0,0,0,0.85)] p-1'
      }`}
    >
      {/* Table Background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 50% 40%, #1c664b 0%, #0f4531 45%, #082d20 80%, #041811 100%)',
          boxShadow: 'inset 0 0 100px rgba(0, 0, 0, 0.75)',
        }}
      >
        <div
          className="absolute inset-0 opacity-8 pointer-events-none"
          style={{
            backgroundImage: `radial-gradient(#ffffff 1px, transparent 1px)`,
            backgroundSize: '24px 24px',
          }}
        />
      </div>

      {/* 6-Player Pure Regular Hexagonal Table Background Overlay (All edges and angles equal) */}
      {is6p && (
        <div className="absolute inset-0 pointer-events-none z-0 flex items-center justify-center">
          <svg
            viewBox="0 0 100 100"
            className="w-full h-full max-w-4xl max-h-full opacity-45 drop-shadow-[0_0_24px_rgba(245,158,11,0.4)]"
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id="hexGoldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#f59e0b" />
                <stop offset="50%" stopColor="#fde68a" />
                <stop offset="100%" stopColor="#d97706" />
              </linearGradient>
            </defs>
            {/* Pure Regular Hexagon Outer Trim (Side length = 46.0, Angle = 120°) */}
            <polygon
              points="27,10.16 73,10.16 96,50 73,89.84 27,89.84 4,50"
              fill="rgba(4, 35, 23, 0.6)"
              stroke="url(#hexGoldGrad)"
              strokeWidth="1.6"
            />
            {/* Pure Regular Hexagon Inner Concentric Ring */}
            <polygon
              points="29,13.63 71,13.63 92,50 71,86.37 29,86.37 8,50"
              fill="none"
              stroke="rgba(245, 158, 11, 0.35)"
              strokeWidth="0.8"
              strokeDasharray="4 2"
            />
          </svg>
        </div>
      )}

      {/* Top Bar: North Seats & Tools */}
      <div className="relative z-20 px-1 pt-0.5 flex items-center justify-between shrink-0 h-11 sm:h-13">
        {/* Quick Tools */}
        <div className="flex items-center space-x-1 bg-slate-950/80 backdrop-blur-md p-0.5 rounded-xl border border-emerald-500/30 shadow">
          <button
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            className="p-1 bg-slate-900 hover:bg-slate-800 text-amber-400 rounded-lg shadow"
            title="快捷表情"
          >
            <Smile className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setShowVoicePicker(!showVoicePicker)}
            className="p-1 bg-slate-900 hover:bg-slate-800 text-sky-400 rounded-lg shadow"
            title="实战语音交流"
          >
            <MessageCircle className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={handleToggleSound}
            className="p-1 bg-slate-900 hover:bg-slate-800 text-slate-300 rounded-lg shadow"
            title={isMuted ? '开启音效' : '静音'}
          >
            {isMuted ? <VolumeX className="w-3.5 h-3.5 text-rose-400" /> : <Volume2 className="w-3.5 h-3.5" />}
          </button>
          <button
            onClick={() => setShowCardBackModal(true)}
            className="p-1 bg-slate-900 hover:bg-slate-800 text-amber-300 rounded-lg shadow border border-amber-500/20"
            title="定制专属牌背与Logo"
          >
            <Palette className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={handleToggleLandscape}
            className={`px-1.5 py-0.5 rounded-lg text-[9px] sm:text-[10px] font-black flex items-center gap-0.5 shadow ${
              isLandscapeMode
                ? 'bg-amber-500 text-slate-950'
                : 'bg-slate-900 text-amber-400 border border-amber-500/30'
            }`}
            title={isLandscapeMode ? t.exitLandscape : t.modeLandscape}
          >
            {isLandscapeMode ? <Minimize2 className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />}
            <span className="hidden sm:inline">{isLandscapeMode ? t.exitLandscape : t.modeLandscape}</span>
          </button>
          {onToggleGodMode && (
            <button
              onClick={onToggleGodMode}
              className={`p-1 rounded-lg shadow ${
                isGodMode ? 'bg-amber-500 text-slate-950 font-black' : 'bg-slate-900 text-slate-400'
              }`}
              title="上帝视角"
            >
              {isGodMode ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            </button>
          )}
        </div>

        {/* Top North Seat: Vertex 1 (12:00) */}
        {!is6p ? (
          <div className="flex flex-col items-center">
            {renderSeatAvatar(2)}
            <div className="h-5 flex items-center justify-center">
              {renderSeatTrickAction(2)}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            {renderSeatAvatar(3)}
            <div className="h-5 flex items-center justify-center">{renderSeatTrickAction(3)}</div>
          </div>
        )}

        {/* Grade Indicator */}
        <div className="bg-slate-950/80 backdrop-blur-md px-2 py-0.5 rounded-xl border border-amber-500/40 shadow flex items-center space-x-1 text-[9px] sm:text-[10px]">
          <Crown className="w-3 h-3 text-amber-400" />
          <span className="text-white font-black">打【{levelRank}】</span>
          <span className="text-rose-400 font-bold">♥{levelRank}配</span>
        </div>
      </div>

      {/* 6-Player Hex Upper Flanks (Vertex 6: 10:00 西北 & Vertex 2: 2:00 东北) */}
      {is6p && (
        <div className="relative z-15 flex items-center justify-between px-2 sm:px-8 -mt-0.5 shrink-0">
          {/* Top-Left: Seat 4 (西北·搭档2 🤝) */}
          <div className="flex items-center space-x-1">
            {renderSeatAvatar(4)}
            <div className="min-w-[45px] sm:min-w-[55px] flex items-center justify-center">
              {renderSeatTrickAction(4)}
            </div>
          </div>

          {/* Top-Right: Seat 2 (东北·搭档1 🤝) */}
          <div className="flex items-center space-x-1">
            <div className="min-w-[45px] sm:min-w-[55px] flex items-center justify-center">
              {renderSeatTrickAction(2)}
            </div>
            {renderSeatAvatar(2)}
          </div>
        </div>
      )}

      {/* Center Area: West Seat + West Drop | Center Status Podium | East Drop + East Seat */}
      <div className="relative z-10 flex-1 flex items-center justify-between px-1 sm:px-4 min-h-0">
        {/* West / Southwest Seat + Drop Zone */}
        <div className={`flex items-center space-x-1 ${is6p ? 'pl-1 sm:pl-6' : ''}`}>
          {renderSeatAvatar(is6p ? 5 : 3)}
          <div className="min-w-[45px] sm:min-w-[55px] flex items-center justify-center">
            {renderSeatTrickAction(is6p ? 5 : 3)}
          </div>
        </div>

        {/* Center Podium: Dealing Animation or Match Status & South's Last Played Cards */}
        {isDealing ? (
          <div
            onClick={handleSkipDealing}
            className="flex flex-col items-center justify-center p-2 rounded-2xl bg-black/60 border border-amber-500/50 backdrop-blur-md shadow-2xl mx-auto cursor-pointer animate-fade-in z-30 select-none group"
            title="点击跳过发牌动画"
          >
            {/* Animated 3D Dealing Deck with flying cards */}
            <div className="relative flex items-center justify-center my-1">
              <div className="absolute w-16 h-16 rounded-full bg-amber-400/25 blur-lg animate-pulse" />

              {/* Center Deck Stack */}
              <div
                className="w-9 h-12 bg-gradient-to-tr from-amber-700 via-amber-500 to-amber-300 rounded-lg border-2 border-amber-200 shadow-2xl flex items-center justify-center select-none"
                style={{ animation: 'dealDeckPulse 1.2s ease-in-out infinite' }}
              >
                <span className="text-xs font-black text-slate-950">🎴</span>
              </div>

              {/* Flying Cards to North, South, West, East */}
              <div
                className="absolute w-6 h-8 bg-white rounded border border-slate-400 shadow pointer-events-none"
                style={{ animation: 'dealFlyNorth 0.7s ease-out infinite' }}
              />
              <div
                className="absolute w-6 h-8 bg-white rounded border border-slate-400 shadow pointer-events-none"
                style={{ animation: 'dealFlySouth 0.7s ease-out infinite 0.18s' }}
              />
              <div
                className="absolute w-6 h-8 bg-white rounded border border-slate-400 shadow pointer-events-none"
                style={{ animation: 'dealFlyWest 0.7s ease-out infinite 0.36s' }}
              />
              <div
                className="absolute w-6 h-8 bg-white rounded border border-slate-400 shadow pointer-events-none"
                style={{ animation: 'dealFlyEast 0.7s ease-out infinite 0.54s' }}
              />
            </div>

            {/* Dealing Status Pill */}
            <div className="mt-1 bg-amber-500/20 text-amber-300 border border-amber-500/40 px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] font-black flex items-center space-x-1">
              <Sparkles className="w-2.5 h-2.5 text-amber-400 animate-spin" />
              <span>正在发牌 ({dealProgress}/{userHand.length})</span>
              <span className="text-[8px] text-slate-400 group-hover:text-amber-200 underline ml-0.5">跳过</span>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-0.5 px-2 rounded-xl bg-black/45 border border-emerald-500/30 backdrop-blur-md shadow-xl mx-auto max-w-xs text-center">
            <div className="flex items-center space-x-1 text-[9px] sm:text-[10px] font-black">
              <span className="text-amber-400">⚡ 轮到：</span>
              <span
                className={`px-1 py-0.1 rounded ${
                  isMyTurn ? 'bg-amber-500 text-slate-950 font-black' : 'text-emerald-300 font-extrabold'
                }`}
              >
                {seatNames[currentTurn]?.name || `Seat ${currentTurn}`}
              </span>
            </div>

            {currentCombo ? (
              <div className="mt-0.5 flex items-center space-x-1 text-[8px] sm:text-[9px] bg-amber-500/20 text-amber-200 px-1.5 py-0.1 rounded-full border border-amber-500/40">
                <Sparkles className="w-2.5 h-2.5 text-amber-400" />
                <span>需压过：{describeCombo(currentCombo)}</span>
                {currentCombo.isBomb && <span className="text-rose-400 font-black">🔥</span>}
              </div>
            ) : (
              <div className="mt-0.5 text-[8px] text-emerald-400 font-bold">
                ✨ 新一墩 · 自由首发
              </div>
            )}

            {/* South Last Action In Center */}
            <div className="mt-0.5 h-5 flex items-center justify-center">
              {renderSeatTrickAction(0)}
            </div>
          </div>
        )}

        {/* East / Southeast Drop Zone + East Seat */}
        <div className={`flex items-center space-x-1 ${is6p ? 'pr-1 sm:pr-6' : ''}`}>
          <div className="min-w-[45px] sm:min-w-[55px] flex items-center justify-center">
            {renderSeatTrickAction(1)}
          </div>
          {renderSeatAvatar(1)}
        </div>
      </div>

      {/* Bottom Area: Action Bar & Hand Cards (Directly adjacent, tight, zero empty gap) */}
      <div className="relative z-20 px-1 pb-0.5 flex flex-col items-center w-full shrink-0 space-y-0.5">
        {/* iOS 3D Tactile Action Bar */}
        <div className="flex flex-wrap items-center justify-center gap-1 bg-slate-950/90 backdrop-blur-xl p-0.5 sm:p-1 rounded-xl border border-amber-500/40 shadow-xl">
          <div className="flex items-center bg-slate-900 p-0.5 rounded-lg border border-slate-800 text-[9px] sm:text-[10px] font-black">
            <button
              onClick={() => handleSelectGroupingMode('natural')}
              className={`px-1.5 py-0.5 rounded transition-all ${
                groupingMode === 'natural' ? 'bg-amber-500 text-slate-950 font-black' : 'text-slate-400'
              }`}
            >
              1.大到小
            </button>
            <button
              onClick={() => handleSelectGroupingMode('coach')}
              className={`px-1.5 py-0.5 rounded transition-all ${
                groupingMode === 'coach' ? 'bg-amber-500 text-slate-950 font-black' : 'text-slate-400'
              }`}
            >
              2.教练建议
            </button>
            <button
              onClick={() => handleSelectGroupingMode('manual')}
              className={`px-1.5 py-0.5 rounded transition-all ${
                groupingMode === 'manual' ? 'bg-sky-500 text-slate-950 font-black' : 'text-slate-400'
              }`}
            >
              3.自选组牌
            </button>
          </div>

          {groupingMode === 'manual' && (
            <div className="flex items-center space-x-0.5">
              <button
                onClick={handleCreateCustomGroup}
                disabled={selectedIds.size === 0}
                className={`px-1.5 py-0.5 rounded text-[9px] font-black ${
                  selectedIds.size > 0 ? 'bg-sky-500 text-slate-950' : 'bg-slate-800 text-slate-500'
                }`}
              >
                编入组({selectedIds.size})
              </button>
              {customGroups.length > 0 && (
                <button
                  onClick={handleResetCustomGroups}
                  className="px-1 py-0.5 rounded text-[9px] text-rose-300 bg-rose-950/60"
                >
                  还原
                </button>
              )}
            </div>
          )}

          <button
            onClick={onAutoHint}
            disabled={!isMyTurn}
            className={`px-2 py-0.5 sm:py-1 rounded-lg text-[10px] sm:text-xs font-black flex items-center space-x-0.5 ${
              isMyTurn ? 'bg-sky-500 hover:bg-sky-400 text-white' : 'bg-slate-800 text-slate-500'
            }`}
          >
            <Sparkles className="w-3 h-3" />
            <span>提示</span>
          </button>

          <button
            onClick={onPass}
            disabled={!isMyTurn || !currentCombo}
            className={`px-2 py-0.5 sm:py-1 rounded-lg text-[10px] sm:text-xs font-black flex items-center space-x-0.5 ${
              isMyTurn && currentCombo ? 'bg-rose-600 hover:bg-rose-500 text-white' : 'bg-slate-800 text-slate-500'
            }`}
          >
            <X className="w-3 h-3" />
            <span>{t.passBtn}</span>
          </button>

          <button
            onClick={onPlay}
            disabled={!isMyTurn || selectedIds.size === 0}
            className={`px-3 sm:px-4 py-0.5 sm:py-1 rounded-lg text-[11px] sm:text-xs font-black flex items-center space-x-1 ${
              isMyTurn && selectedIds.size > 0
                ? 'bg-gradient-to-r from-amber-400 to-amber-500 text-slate-950 shadow ring-1 ring-amber-200'
                : 'bg-slate-800 text-slate-500'
            }`}
          >
            <Zap className="w-3.5 h-3.5 fill-current" />
            <span>出牌 ({selectedIds.size})</span>
          </button>
        </div>

        {/* User Hand Cards View: Always Strictly In Viewport Without Scrolling */}
        <div className="w-full flex justify-center items-end h-[56px] sm:h-[72px] md:h-[84px] px-0.5 overflow-x-hidden overflow-y-visible shrink-0">
          {groupingMode === 'natural' && renderAdaptiveCards(naturalSortedHand)}

          {groupingMode === 'coach' && (
            <div className="flex flex-nowrap items-end justify-center gap-0.5 sm:gap-1 max-w-full overflow-x-hidden p-0.5">
              {bestGroups.map((group, gIdx) => (
                <div
                  key={gIdx}
                  className="flex items-end -space-x-4 sm:-space-x-5 p-0.5 bg-black/25 rounded-xl border border-amber-500/20 shrink-0"
                >
                  {group.cards.map((c) => (
                    <PlayingCard
                      key={c.id}
                      card={c}
                      levelRank={levelRank}
                      isSelected={selectedIds.has(c.id)}
                      onClick={() => onToggleCard(c.id)}
                      size="md"
                    />
                  ))}
                </div>
              ))}
            </div>
          )}

          {groupingMode === 'manual' && (
            <div className="flex flex-nowrap items-end justify-center gap-0.5 sm:gap-1 max-w-full overflow-x-hidden p-0.5">
              {customGroups.map((group, gIdx) => (
                <div
                  key={gIdx}
                  className="relative flex items-end -space-x-4 sm:-space-x-5 p-0.5 bg-sky-950/40 rounded-xl border border-sky-500/30 shrink-0"
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDissolveCustomGroup(gIdx);
                    }}
                    className="absolute -top-2 left-1/2 -translate-x-1/2 bg-sky-900 text-white text-[8px] px-1 rounded-full border border-sky-400 z-30"
                  >
                    ✕
                  </button>
                  {group.map((c) => (
                    <PlayingCard
                      key={c.id}
                      card={c}
                      levelRank={levelRank}
                      isSelected={selectedIds.has(c.id)}
                      onClick={() => onToggleCard(c.id)}
                      size="md"
                    />
                  ))}
                </div>
              ))}

              {ungroupedCards.length > 0 && (
                <div className="relative flex items-end -space-x-4 sm:-space-x-5 p-0.5 bg-black/20 rounded-xl border border-white/10 shrink-0">
                  {ungroupedCards.map((c) => (
                    <PlayingCard
                      key={c.id}
                      card={c}
                      levelRank={levelRank}
                      isSelected={selectedIds.has(c.id)}
                      onClick={() => onToggleCard(c.id)}
                      size="md"
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showEmojiPicker && (
        <div className="absolute top-10 left-2 z-50 bg-slate-900/95 border border-amber-500/40 rounded-xl p-1.5 shadow-2xl space-y-1 max-w-xs">
          <div className="flex items-center justify-between text-xs font-black text-amber-300">
            <span>表情</span>
            <button onClick={() => setShowEmojiPicker(false)} className="text-slate-400">✕</button>
          </div>
          <div className="grid grid-cols-4 gap-1">
            {POPULAR_EMOJIS.map((item, idx) => (
              <button key={idx} onClick={() => handleSelectEmoji(item)} className="p-1 rounded text-base bg-slate-800">
                {item.emoji}
              </button>
            ))}
          </div>
        </div>
      )}

      {showVoicePicker && (
        <div className="absolute top-10 left-2 z-50 bg-slate-900/95 border border-sky-500/40 rounded-xl p-1.5 shadow-2xl space-y-0.5 max-w-xs">
          <div className="flex items-center justify-between text-xs font-black text-sky-300">
            <span>实战牌语</span>
            <button onClick={() => setShowVoicePicker(false)} className="text-slate-400">✕</button>
          </div>
          <div className="space-y-0.5 max-h-36 overflow-y-auto">
            {POPULAR_PHRASES.map((phrase) => (
              <button
                key={phrase.id}
                onClick={() => handleSelectPhrase(phrase)}
                className="w-full text-left text-[10px] bg-slate-800 p-1 rounded text-slate-200"
              >
                {phrase.text}
              </button>
            ))}
          </div>
        </div>
      )}

      <CardBackCustomizerModal isOpen={showCardBackModal} onClose={() => setShowCardBackModal(false)} />
    </div>
  );
};
